import { getCurrentInstance, ref } from 'vue'
import { useRoute } from 'vue-router'
import axios from 'axios'
import qs from 'qs'
import Swal from 'sweetalert2'
import { useLeadGeneratorStore } from '@/stores/leadGenerator'
import { normalizeCustomizationFromBranding } from '@/utils/customization'
import { applyThemeOverrides } from '@/utils/themeOverrides'

const BRANDING_API_PATH = '/scripts/proposals/rmd/get-client-branding.php'
const BRANDING_API_ORIGIN =
  import.meta.env.VITE_BRANDING_API_ORIGIN || 'https://beta.stonewoodfinancial.com'

const EMPTY_CUSTOMIZATION_IDS = new Set(['', 'null', 'undefined'])

const headerLogo = ref('')
const companyName = ref('')
const companyWebsite = ref('')
const gtm_code = ref('')
const dialog = ref(false)
const isBrandingLoaded = ref(false)
const isBrandingLoading = ref(false)
const brandingCustomizationId = ref('')

let activeBrandingRequest = null
let activeRequestSignature = ''
let blockingAlertShown = false

function getBrandingApiUrl() {
  if (import.meta.env.DEV) return BRANDING_API_PATH

  return `${BRANDING_API_ORIGIN.replace(/\/$/, '')}${BRANDING_API_PATH}`
}

function normalizeBrandingResponse(responseData) {
  if (!responseData) return {}
  if (typeof responseData === 'object') return responseData

  if (typeof responseData === 'string') {
    const trimmedData = responseData.trim()

    try {
      return JSON.parse(trimmedData)
    } catch {
      const jsonStart = trimmedData.indexOf('{')
      const jsonEnd = trimmedData.lastIndexOf('}')

      if (jsonStart >= 0 && jsonEnd > jsonStart) {
        try {
          return JSON.parse(trimmedData.slice(jsonStart, jsonEnd + 1))
        } catch {
          return {}
        }
      }
    }
  }

  return {}
}

function hasBrandingData(data) {
  return Boolean(data && typeof data === 'object' && Object.keys(data).length)
}

function showBlockingAccessMessage() {
  dialog.value = true

  if (blockingAlertShown) return
  blockingAlertShown = true

  Swal.fire({
    title: 'You Need access',
    text: 'This page is no longer active.',
    icon: 'warning',
    backdrop: true,
    allowOutsideClick: false,
    allowEscapeKey: false,
    allowEnterKey: false,
    showConfirmButton: false,
    heightAuto: false,
    didOpen: () => {
      const container = Swal.getContainer()
      if (container) container.style.zIndex = '2147483647'
    },
  })
}

function clearBlockingAccessMessage() {
  dialog.value = false

  if (!blockingAlertShown) return
  blockingAlertShown = false
  Swal.close()
}

function normalizeLeadAllow(value) {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value === 1
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (['1', 'true', 'yes', 'y'].includes(normalized)) return true
    if (['0', 'false', 'no', 'n'].includes(normalized)) return false
  }
  return null
}

function normalizeQueryValue(value) {
  const normalized = Array.isArray(value) ? value[0] : value
  return String(normalized || '').trim()
}

function normalizeCustomizationId(value) {
  const normalized = normalizeQueryValue(value)
  if (EMPTY_CUSTOMIZATION_IDS.has(normalized.toLowerCase())) return ''
  return normalized
}

function isCustomizeModeFromQuery(query = {}) {
  return normalizeQueryValue(query.customize).toLowerCase() === 'true'
}

export function useBranding() {
  const store = useLeadGeneratorStore()
  const route = getCurrentInstance() ? useRoute() : null

  async function loadBranding(options = {}) {
    const querySource =
      options?.query && typeof options.query === 'object' && Object.keys(options.query).length
        ? options.query
        : route?.query || {}

    const queryParams = {}
    const routeU = normalizeQueryValue(querySource.U ?? querySource.u)
    const routeC = normalizeQueryValue(querySource.C ?? querySource.c)
    const routeSessionId = normalizeQueryValue(querySource.session_id ?? querySource['amp;session_id'])

    if (routeU) sessionStorage.setItem('rtb_u', routeU)
    if (routeC) sessionStorage.setItem('rtb_c', routeC)
    if (routeSessionId) sessionStorage.setItem('rtb_session_id', routeSessionId)

    const persistedU = sessionStorage.getItem('rtb_u') || ''
    const persistedC = sessionStorage.getItem('rtb_c') || ''
    const persistedSessionId = sessionStorage.getItem('rtb_session_id') || ''

    const resolvedU = routeU || persistedU
    const resolvedC = routeC || persistedC
    const resolvedSessionId = routeSessionId || persistedSessionId
    const preserveLocalCustomization = Boolean(
      options?.preserveLocalCustomization ??
      (isCustomizeModeFromQuery(querySource) && store.customizationLoaded)
    )

    if (resolvedU) queryParams.u = resolvedU
    if (resolvedC) queryParams.c = resolvedC

    const hasIdentity = Boolean(queryParams.u || queryParams.c)

    const requestBody = qs.stringify(queryParams)
    const requestSignature = requestBody || 'anonymous'

    if (!options?.force && activeBrandingRequest && activeRequestSignature === requestSignature) {
      return activeBrandingRequest
    }

    // Force-refresh: discard any in-flight request so a fresh one is always made
    if (options?.force) {
      activeBrandingRequest = null
      activeRequestSignature = ''
    }

    activeRequestSignature = requestSignature
    isBrandingLoading.value = true

    activeBrandingRequest = (async () => {
      const response = await axios.post(
        getBrandingApiUrl(),
        requestBody,
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
      )

      const data = normalizeBrandingResponse(response.data)

      if (!hasBrandingData(data)) {
        showBlockingAccessMessage()
        return null
      }

      const leadAllow = normalizeLeadAllow(data.lead_allow)

      headerLogo.value = data.company_logo || ''
      companyName.value = data.company_name || 'Discover your Retirement Tax Bill'
      companyWebsite.value = data.company_website || ''
      gtm_code.value = data.gtm_code || ''

      // Persist to Pinia store
      store.company.company_name = companyName.value
      store.company.company_logo = headerLogo.value
      store.company.company_website = companyWebsite.value
      store.company.advisor_email = data.advisor_email || data.email || ''
      store.company.userFullName = data.userFullName || ''
      store.company.phone = data.phone || ''
      store.company.lead_capture_disclosure = data.lead_capture_disclosure || ''
      store.company.meeting_url = data.lead_capture_meet_url || ''
      store.company.gtm_code = gtm_code.value
      store.company.lead_allow = leadAllow

      const customizationId = normalizeCustomizationId(data.customization_id)
      brandingCustomizationId.value = customizationId

      // Normalize customization from branding response
      // When customization_id is present, get-client-branding.php returns the full customization data
      const customization = normalizeCustomizationFromBranding(data)

      if (import.meta.env.DEV && customizationId) {
        console.debug(`[Branding] Customization loaded: ${customizationId}`)
      }

      if (preserveLocalCustomization) {
        store.ensureCustomizationDefaults()
      } else {
        store.applyCustomizationPayload(customization)
      }

      applyThemeOverrides(store.customization.theme)

      if (hasIdentity && leadAllow === false) {
        showBlockingAccessMessage()
      } else {
        clearBlockingAccessMessage()
      }

      if (data.gtm_code) {
        injectGTM(data.gtm_code)
      }

      isBrandingLoaded.value = true
      return data
    })()

    try {
      return await activeBrandingRequest
    } catch (error) {
      console.error('Branding fetch failed:', error)
      showBlockingAccessMessage()
      return null
    } finally {
      isBrandingLoading.value = false
      activeBrandingRequest = null
    }
  }

  function injectGTM(id) {
    if (document.querySelector(`script[data-gtm="${id}"]`)) return
    ;(function (w, d, s, l, i) {
      w[l] = w[l] || []
      w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' })
      const f = d.getElementsByTagName(s)[0]
      const j = d.createElement(s)
      const dl = l !== 'dataLayer' ? '&l=' + l : ''
      j.async = true
      j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl
      j.setAttribute('data-gtm', i)
      f.parentNode.insertBefore(j, f)
    })(window, document, 'script', 'dataLayer', id)
  }

  function getGTMIFrameUrl() {
    return `https://www.googletagmanager.com/ns.html?id=${gtm_code.value}`
  }

  function formatURL(url) {
    if (!url) return ''
    url = url.trim()
    if (/^https?:\/\//i.test(url)) return url
    try {
      return new URL('http://' + url).href
    } catch {
      return ''
    }
  }

  return {
    headerLogo,
    companyName,
    companyWebsite,
    gtm_code,
    dialog,
    isBrandingLoaded,
    isBrandingLoading,
    brandingCustomizationId,
    loadBranding,
    getGTMIFrameUrl,
    formatURL,
  }
}
